import type { ReactNode } from 'react'
import type { Project } from '../types'

import { galleryFor } from '../data'
import { useDesk95Theme } from '../theme'

type ProjectViewerProps = {
    project: Project
}

export const ProjectViewer = ({ project }: ProjectViewerProps): ReactNode => {
    const palette = useDesk95Theme()

    const brief = [
        { label: 'Challenge', value: project.challenge },
        { label: 'Direction', value: project.direction },
        { label: 'Outcome', value: project.outcome }
    ]

    const gallery = galleryFor(project)
    const rule = { borderTop: `1px solid ${palette.hairline}` }

    return (
        <div
            className='desk95-scroll size-full overflow-y-auto'
            style={{ background: palette.face, color: palette.text }}
        >
            <div className='p-[8px]'>
                <img
                    src={project.image}
                    alt={project.alt}
                    className='block h-[200px] w-full object-cover'
                    draggable={false}
                />

                <h2 className='mt-[13px] text-[14px] leading-[1.2]'>
                    {project.headline}
                </h2>

                <p className='mt-[12px] text-[11px] italic' style={{ color: palette.disabledText }}>
                    {project.positioning}
                </p>

                <dl
                    className='mt-[9px] grid grid-cols-1 gap-x-[10px] gap-y-[12px] pt-[10px] pb-[9px] sm:grid-cols-3'
                    style={{ ...rule, borderBottom: `1px solid ${palette.hairline}` }}
                >
                    {brief.map(item => (
                        <div key={item.label} className='px-[4px]'>
                            <dt className='text-[11px] leading-[16px]'>
                                {item.label}
                            </dt>

                            <dd className='mt-[6px] text-[11px] leading-[16px]'>
                                {item.value}
                            </dd>
                        </div>
                    ))}
                </dl>

                <div className='mt-[12px] flex flex-col gap-[8px] text-[11px] leading-[16px]'>
                    <p>{project.intro}</p>
                    <p>{project.description}</p>
                    <p>{project.note}</p>
                </div>

                <div
                    className='mt-[14px] h-[21px] px-[6px] text-[11px] leading-[21px] font-bold'
                    style={{
                        background: palette.sectionBar,
                        color: palette.sectionBarText,
                        boxShadow: `inset 1px 1px 0 0 ${palette.hairline}, inset -1px -1px 0 0 ${palette.highlight}`
                    }}
                >
                    Gallery
                </div>

                <ul className='mt-[8px] grid grid-cols-[repeat(auto-fit,minmax(110px,1fr))] gap-[8px]'>
                    {gallery.map(item => (
                        <li key={item.id}>
                            <img
                                src={item.image}
                                alt={item.alt}
                                className='block h-[90px] w-full object-cover'
                                draggable={false}
                            />
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    )
}
